import type { View } from '../app/views/types';
import { useState, useEffect } from 'react';
import { Storage } from '../lib/storage';
import type { SetToast } from './toast';

// ─────────────────────────────────────────────────────────────────────
// useDrafts — the saved-setup ("drafts") list shown on the Home hub. Owns the
// `drafts` state + its refresh-on-home effect + the 3 handlers (refresh/delete/
// open). Extracted verbatim from `Padel24AppMain` in the former monolith
// (legacy/App.jsx) as the first slice of the AppShell/god-component collapse
// (PR-AQ). No persisted state of
// its own beyond reading the per-format `*_setups_v1` Storage keys; the nav
// setters it needs are passed in via `deps`.
// ─────────────────────────────────────────────────────────────────────
export interface UseDraftsDeps {
  view: string;
  setView: (v: View) => void;
  setPendingSetupLoad: (v: any) => void;
  setSetupFromActivities: (v: any) => void;
  setCustomActivityType: (v: any) => void;
  /** The store's toast slot — where a deletion that did not land is reported (#1329). */
  setToast: SetToast;
}

export function useDrafts({
  view,
  setView,
  setPendingSetupLoad,
  setSetupFromActivities,
  setCustomActivityType,
  setToast,
}: UseDraftsDeps) {
  const [drafts, setDrafts] = useState<any[]>([]);

  const refreshDrafts = async () => {
    const sources = [
      { key: 'padel24_setups_v1', type: 'padel24', typeLabel: 'Padel24' },
      { key: 'americano_setups_v1', type: 'americano', typeLabel: 'Americano' },
      { key: 'team_americano_setups_v1', type: 'team_americano', typeLabel: 'Team Americano' },
      { key: 'mexicano_setups_v1', type: 'mexicano', typeLabel: 'Mexicano' },
      { key: 'mixicano_setups_v1', type: 'mixicano', typeLabel: 'Mix Mexicano' },
      { key: 'team_mexicano_setups_v1', type: 'team_mexicano', typeLabel: 'Team Mexicano' },
      { key: 'mix_americano_setups_v1', type: 'mix_americano', typeLabel: 'Mix Americano' },
      { key: 'club_americano_setups_v1', type: 'club_americano', typeLabel: 'Club Americano' },
      {
        key: 'club_team_americano_setups_v1',
        type: 'club_team_americano',
        typeLabel: 'Club Team Americano',
      },
      { key: 'club_mexicano_setups_v1', type: 'club_mexicano', typeLabel: 'Club Mexicano' },
      { key: 'klubbmesterskap_setups_v1', type: 'klubbmesterskap', typeLabel: 'Klubbmesterskap' },
    ];
    // ONE LISTING, THEN ONLY WHAT EXISTS (#1145, AUD-031). Reading all eleven
    // keys answered a 404 for every format the club never saved a setup for —
    // eleven red lines in every portal console, burying real errors and riding
    // along in every bug report. The club's key list is one request and says
    // which are there.
    //
    // A READ THAT FAILED IS NOT «NOTHING STORED» (#1349 review). A failed
    // listing used to read as an empty key set, and a failed read of one key
    // as «no setups there», so the list emptied. Harmless on first load (there
    // was nothing to lose), but the deletion re-reads the list after a failure
    // too, and then a failed re-read showed NO setups although none was
    // deleted. Now what could not be read keeps what was last read: all of it
    // for a failed listing, that key's setups for a failed key. Answers
    // whether the list is FRESH; the caller decides whether to say so.
    let stored: Set<string> | null = null;
    try {
      const listing = await Storage.list('');
      if (!listing.failed) stored = new Set(listing.keys ?? []);
    } catch {
      // Unanswered: stored stays null.
    }
    if (stored === null) return false;
    const all: any[] = [];
    const unread = new Set<string>();
    for (const src of sources) {
      if (!stored.has(src.key)) continue;
      const res = await Storage.read(src.key);
      if (res.status === 'failed') {
        unread.add(src.key);
        continue;
      }
      if (res.status !== 'ok') continue;
      try {
        const list = JSON.parse(res.value);
        if (Array.isArray(list)) {
          for (const s of list) {
            // Each setup has: id, name, config, selectedPlayerIds, customPlayers,
            // playerOverrides, and optionally proposedGroups, lockedPlayers.
            const playerCount = Array.isArray(s.selectedPlayerIds) ? s.selectedPlayerIds.length : 0;
            all.push({
              key: src.key,
              setupId: s.id,
              type: src.type,
              typeLabel: src.typeLabel,
              name: s.name || src.typeLabel,
              playerCount,
              hasGroups: !!(s.proposedGroups && s.proposedGroups.length > 0),
              savedAt: s.updatedAt || s.createdAt || 0,
            });
          }
        }
      } catch {
        // Ignore corrupt data: it is what is stored, and it cannot be shown.
      }
    }
    setDrafts((prev) => {
      const next = [...all, ...prev.filter((d) => unread.has(d.key))];
      // Sort by last saved (newest first)
      next.sort((a, b) => (b.savedAt || 0) - (a.savedAt || 0));
      return next;
    });
    return unread.size === 0;
  };

  // ── A DELETION THAT DID NOT LAND IS SAID (#1329) ──
  //
  // A failed write used to be a `console.warn` and nothing else: the setup
  // stayed in the list, and reading it as «I pressed delete and it is still
  // there» is a bug report. Now the person is told, and the list is re-read
  // either way, so it shows what is STORED — the setup back where it was after
  // a refused write, or gone if an ambiguous failure did land. The re-read
  // (`Storage.get` inside `refreshDrafts`) also clears the must-refresh a failed
  // write leaves, so «prøv igjen» can actually be tried.
  //
  // `read`, not `get`, for the list being edited: `get` answers null for a
  // failed request too, and «nothing stored» must not be how a failed read
  // reports a deletion that never happened.
  //
  // Answers whether the deletion landed.
  const deleteDraft = async (draft): Promise<boolean> => {
    let landed = false;
    try {
      const res = await Storage.read(draft.key);
      if (res.status === 'failed') throw new Error(`read of ${draft.key} failed`);
      if (res.status === 'ok') {
        const list = JSON.parse(res.value);
        if (Array.isArray(list)) {
          const updated = list.filter((s) => s.id !== draft.setupId);
          await Storage.set(draft.key, JSON.stringify(updated));
        }
      }
      landed = true;
    } catch (e) {
      console.warn('[Setups] Kunne ikke slette oppsett:', e);
    }
    const fresh = await refreshDrafts();
    // A deletion that LANDED is known even when the re-read is not: the write
    // was confirmed, so that setup is gone (storage-first — state after the
    // write). Everything else stays as last read.
    if (landed && !fresh) {
      setDrafts((prev) =>
        prev.filter((d) => !(d.key === draft.key && d.setupId === draft.setupId)),
      );
    }
    // One toast, the one that says all of it.
    if (!landed) {
      setToast({
        type: 'error',
        message: '',
        messageKey: fresh ? 'home.drafts.deleteFailed' : 'home.drafts.deleteFailedUnread',
      });
    } else if (!fresh) {
      setToast({ type: 'error', message: '', messageKey: 'home.drafts.refreshFailed' });
    }
    return landed;
  };

  const openDraft = (draft) => {
    setSetupFromActivities(false);
    setCustomActivityType(null);
    setPendingSetupLoad({ key: draft.key, setupId: draft.setupId });
    if (draft.type === 'padel24') setView('wizard');
    else if (draft.type === 'americano') setView('americanoSetup');
    else if (draft.type === 'team_americano') setView('teamAmericanoSetup');
    else if (draft.type === 'mexicano') setView('mexicanoSetup');
    else if (draft.type === 'mixicano') setView('mixicanoSetup');
    else if (draft.type === 'team_mexicano') setView('teamMexicanoSetup');
    else if (draft.type === 'mix_americano') setView('mixAmericanoSetup');
    else if (draft.type === 'club_americano') setView('clubAmericanoSetup');
    else if (draft.type === 'club_team_americano') setView('clubTeamAmericanoSetup');
    else if (draft.type === 'club_mexicano') setView('clubMexicanoSetup');
  };

  useEffect(() => {
    if (view === 'home') {
      refreshDrafts();
    }
  }, [view]);

  return { drafts, refreshDrafts, deleteDraft, openDraft };
}
